import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { paymentOptions } from "@/constants/checkout";
import { ReservationsApiError } from "@/constants/customErrors";
import { Order } from "@/custom-types/Order";
import { HYDRATE, HydrateAction } from "@/redux/reducers/hydration";

export type OrderState = {
  claimReservationFailure: boolean | null;
  claimReservationSuccess: boolean | null;
  data: Order | null;
  loading: {
    getReservation: boolean;
    saveReservation: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  payfirmaScriptStatus: any | null;
  paymentErrorMsg: string;
  reservationId: number | null;
  selectedPaymentOption: string;
};

export const initialState: OrderState = {
  claimReservationFailure: null,
  claimReservationSuccess: null,
  data: null,
  loading: {
    getReservation: false,
    saveReservation: false,
  },
  payfirmaScriptStatus: null,
  paymentErrorMsg: "",
  reservationId: null,
  selectedPaymentOption: paymentOptions.inStore,
};

const orderSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action: HydrateAction) => {
      /**
       * The `order` state is modified in `getServerSideProps` of order status
       * pages, and needs to be loaded here to ensure that data is added to the
       * redux state.
       */
      return {
        /**
         * Retain any state that was added client-side that isn't in the initial state.
         */
        ...state,
        ...action.payload.order,
      };
    });
  },
  initialState,
  name: "order",
  reducers: {
    claimOrderFailure: (state) => {
      state.claimReservationFailure = true;
    },
    claimOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.claimReservationSuccess = true;
      state.data = action.payload;
    },
    getOrderFailure: (state) => {
      state.data = initialState.data;
      state.loading.getReservation = initialState.loading.getReservation;
    },
    getOrderRequest: (state) => {
      state.loading.getReservation = true;
    },
    getOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.data = action.payload;
      state.loading.getReservation = initialState.loading.getReservation;
    },
    saveOrderFailure: (
      state,
      action: PayloadAction<ReservationsApiError[]>,
    ) => {
      state.loading.saveReservation = false;
      const paymentError = action.payload?.find((error: ReservationsApiError) =>
        error.title?.toLowerCase().includes("payment"),
      );
      if (paymentError) {
        state.paymentErrorMsg = paymentError.detail?.split("reason=")[0].trim();
      }
    },
    saveOrderRequest: (state) => {
      state.loading.saveReservation = true;
      state.paymentErrorMsg = initialState.paymentErrorMsg;
    },
    saveOrderSuccess: (state, action: PayloadAction<Order>) => {
      const reservationId = action.payload.id;
      state.data = action.payload;
      state.loading.saveReservation = false;
      state.paymentErrorMsg = initialState.paymentErrorMsg;
      state.reservationId = reservationId;
    },
    setPayfirmaErrorMessage: (
      state,
      action: PayloadAction<{ msg: string }>,
    ) => {
      state.paymentErrorMsg = action.payload.msg;
    },
    setPayfirmaScriptStatus: (
      state,
      action: PayloadAction<{ status: string }>,
    ) => {
      state.payfirmaScriptStatus = action.payload.status;
    },
    setSelectedPaymentOption: (state, action: PayloadAction<string>) => {
      state.paymentErrorMsg =
        action.payload === paymentOptions.inStore
          ? initialState.paymentErrorMsg
          : state.paymentErrorMsg;
      state.selectedPaymentOption = action.payload;
    },
  },
});

export const {
  setPayfirmaScriptStatus,
  setPayfirmaErrorMessage,
  setSelectedPaymentOption,
  saveOrderRequest,
  saveOrderSuccess,
  saveOrderFailure,
  getOrderRequest,
  getOrderSuccess,
  getOrderFailure,
  claimOrderSuccess,
  claimOrderFailure,
} = orderSlice.actions;

export default orderSlice.reducer;
